/* eslint-disable max-lines-per-function */
import {useCallback, useEffect, useState} from 'react';
import {format, isSameDay} from 'date-fns';
import {BookAppointmentSchema} from 'definitions/Yup';
import {appointmentActions, selectAvailability} from 'features/Appointment';
import {selectUserProfile} from 'features/User';
import {useError, useRequesting} from 'hooks';
import {
  AppointmentDurations,
  AppointmentTypes,
  BookAppointmentData,
  SliceStatus,
  UserRoles,
} from 'interfaces';
import {useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {BookingService} from 'services';
import {dateTimeStringCompatibility, isProvider} from 'utils';

import {yupResolver} from '@hookform/resolvers/yup';

const now = new Date();
const nextDay = new Date(now);
nextDay.setDate(now.getDate() + 1);

export function useDirectBookingAvailability(
  appointmentType: AppointmentTypes,
  duration: AppointmentDurations,
  providerId: string,
  providerRole: UserRoles.prescriber | UserRoles.therapist | undefined,
  state: string,
  calendarId: number,
) {
  const availability = useSelector(selectAvailability);
  const user = useSelector(selectUserProfile);

  const [timeArr, setTimeArr] = useState<
    {value: string; rawTime: string; disabled: boolean}[]
  >([]);
  const dispatch = useDispatch();
  const {
    register,
    control,
    formState: {errors},
    watch,
    setValue,
  } = useForm<BookAppointmentData>({
    defaultValues: {
      appointmentType,
      providerId: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase(),
      date: nextDay,
      time: '',
    },
    resolver: yupResolver(BookAppointmentSchema),
  });

  const [date, timezone, time] = watch(['date', 'timezone', 'time']);

  const sliceStatus = useRequesting('appointment');
  const {apiError, resetAsyncError} = useError('appointment');

  const getProviderCalendarData = useCallback(
    (date: Date): void => {
      if (!providerId) {
        return;
      }
      dispatch(
        appointmentActions.getAvailableSlots({
          appointmentType,
          duration,
          yearAndMonth: format(date, 'yyyy-MM'),
          state,
          providerId,
          calendarId,
        }),
      );
    },
    [dispatch, providerId, state],
  );

  const providerCalendar = availability?.slots
    ? availability?.slots[providerId]
    : [];

  const disabledDaysFn = useCallback(() => {
    if (
      providerId &&
      Object.keys(availability?.slots ?? {}).length &&
      Array.isArray(availability.slots[providerId])
    )
      return BookingService.getDisabledDays(
        availability.slots[providerId],
        isProvider(user),
      );
    return [];
  }, [availability]);
  const disabledDays = disabledDaysFn();

  // useEffect(() => setValue('date', nextDay), [setValue]);

  useEffect(() => {
    if (
      availability?.slots &&
      Array.isArray(availability?.slots?.[providerId]) &&
      availability.slots[providerId].length
    ) {
      const currentDay = availability.slots[providerId].find(day => {
        const formattedDate = dateTimeStringCompatibility(day.availabilityDate);
        return isSameDay(new Date(formattedDate), date as Date);
      });
      const hours = BookingService.getAvailableTimeData(
        timezone,
        currentDay?.availabilityTimes || [],
      );
      setTimeArr(hours);
    } else {
      setTimeArr([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, timezone, providerId, availability]);

  useEffect(() => {
    if (
      providerRole === UserRoles.therapist ||
      (providerRole === UserRoles.prescriber && state)
    )
      getProviderCalendarData(new Date());
  }, [appointmentType, getProviderCalendarData, providerRole, state]);

  useEffect(() => {
    return (): void => {
      if (apiError) dispatch(resetAsyncError('appointment'));
    };
  }, [apiError, dispatch, resetAsyncError]);

  return {
    time: timeArr,
    errors,
    register,
    control,
    providerCalendar,
    disabledDays,
    selectedTime: time,
    apiErrorMsg: apiError,
    isLoading: sliceStatus === SliceStatus.pending,
    watch,
    setValue,
    appointmentTypeID: availability?.appointmentTypeId,
    appointmentType,
    duration,
    providerRole,
    state,
    getProviderCalendarData,
    slots: availability?.slots,
    timezone,
  };
}
